// Контент-план: запросы, ключи, свежесть и чтение. Контракт —
// docs/modules/06-kontent-plan.md, «API» и «Обновления и конфликты».
// Изменения с оптимистичным кэшем — planMutations.ts (только в чанке раздела):
// этот файл попадает и в основной бандл, через «В контент-план» в карточке поста.

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { ApiError, request } from './client'
import type {
  Attachment,
  AttachmentCategory,
  Board,
  CardDetail,
  CardSummary,
  PlanCardsParams,
  PlanCardsResponse,
  PlanDraft,
  PlanOverview,
  PlanPlatform,
  PostPlanCard,
  StageKey,
} from './types'

export interface CardPatch {
  title?: string
  body?: string
  notes?: string
  publish_at?: string | null
  published_url?: string | null
  board_id?: number
}

export interface StagePatch {
  assignee_id?: number | null
  start?: string | null
  end?: string | null
}

export interface CreateCardBody {
  board_id: number
  title: string
  stage?: StageKey
  publish_at?: string | null
  body?: string
  notes?: string
  after_id?: number | null
}

function query(params: object): string {
  const q = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '' && value !== false) q.set(key, String(value))
  }
  return q.toString()
}

export const planApi = {
  overview: (archived: boolean, signal?: AbortSignal) =>
    request<PlanOverview>(`/api/plan/boards${archived ? '?archived=true' : ''}`, { signal }),
  createBoard: (body: { platform: PlanPlatform; title: string; voice?: string }) =>
    request<Board>('/api/plan/boards', { method: 'POST', body }),
  updateBoard: (id: number, body: { title?: string; voice?: string; own_source_id?: number | null; position?: number }) =>
    request<Board>(`/api/plan/boards/${id}`, { method: 'PATCH', body }),
  archiveBoard: (id: number) => request<void>(`/api/plan/boards/${id}`, { method: 'DELETE' }),
  restoreBoard: (id: number) => request<Board>(`/api/plan/boards/${id}/restore`, { method: 'POST' }),

  cards: (params: PlanCardsParams, signal?: AbortSignal) =>
    request<PlanCardsResponse>(`/api/plan/cards?${query(params)}`, { signal }),
  createCard: (body: CreateCardBody) => request<CardDetail>('/api/plan/cards', { method: 'POST', body }),
  fromPost: (body: { post_id: number; board_id: number }) =>
    request<{ card: CardDetail; created: boolean }>('/api/plan/cards/from-post', { method: 'POST', body }),
  card: (id: number, signal?: AbortSignal) => request<CardDetail>(`/api/plan/cards/${id}`, { signal }),
  patchCard: (id: number, body: CardPatch) => request<CardDetail>(`/api/plan/cards/${id}`, { method: 'PATCH', body }),
  moveCard: (id: number, body: { stage: StageKey; after_id: number | null; before_id: number | null }) =>
    request<CardSummary>(`/api/plan/cards/${id}/move`, { method: 'POST', body }),
  patchStage: (id: number, stage: StageKey, body: StagePatch) =>
    request<CardSummary>(`/api/plan/cards/${id}/stages/${stage}`, { method: 'PATCH', body }),
  deleteCard: (id: number) => request<void>(`/api/plan/cards/${id}`, { method: 'DELETE' }),
  restoreCard: (id: number) => request<CardDetail>(`/api/plan/cards/${id}/restore`, { method: 'POST' }),
  draft: (id: number) => request<PlanDraft>(`/api/plan/cards/${id}/draft`, { method: 'POST' }),
  addLink: (id: number, body: { url: string; title?: string }) =>
    request<Attachment>(`/api/plan/cards/${id}/links`, { method: 'POST', body }),
  deleteAttachment: (id: number) => request<void>(`/api/plan/attachments/${id}`, { method: 'DELETE' }),
  downloadAttachments: (id: number, category: AttachmentCategory) =>
    request<{ download_url: string }>(`/api/plan/cards/${id}/attachments/download`, { method: 'POST', body: { category } }),
  sendAttachments: (id: number, category: AttachmentCategory) =>
    request<{ sent: number }>(`/api/plan/cards/${id}/attachments/send`, { method: 'POST', body: { category } }),
  sendAttachment: (id: number) =>
    request<{ sent: number }>(`/api/plan/attachments/${id}/send`, { method: 'POST' }),
  postPlan: (postId: number, signal?: AbortSignal) =>
    request<{ cards: PostPlanCard[] }>(`/api/posts/${postId}/plan`, { signal }),
}


export interface CardsKey {
  platform: PlanPlatform
  board: number | null
  from: string | null
  to: string | null
  archive: boolean
}

export const planKeys = {
  all: ['plan'] as const,
  boards: ['plan', 'boards'] as const,
  boardsArchived: ['plan', 'boards', { archived: true }] as const,
  cardsAll: ['plan', 'cards'] as const,
  cards: (k: CardsKey) => ['plan', 'cards', k] as const,
  card: (id: number) => ['plan', 'card', id] as const,
  postPlanAll: ['post-plan'] as const,
  postPlan: (postId: number) => ['post-plan', postId] as const,
}

// Во время переноса интервал стоит, а запросы в полёте отменены:
// пришедший список не дёргает карточку из-под пальца.
let dragging = false

export function setPlanDragging(qc: QueryClient, on: boolean): void {
  if (dragging === on) return
  dragging = on
  if (on) void qc.cancelQueries({ queryKey: planKeys.cardsAll })
}

const FRESH = {
  refetchOnWindowFocus: true,
  refetchInterval: () => (dragging ? false : 30_000),
  staleTime: 5_000,
} as const

export function usePlanOverview({ archived = false, enabled = true }: { archived?: boolean; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: archived ? planKeys.boardsArchived : planKeys.boards,
    queryFn: ({ signal }) => planApi.overview(archived, signal),
    enabled,
    ...FRESH,
    staleTime: 30_000,
  })
}

export function usePlanCards(k: CardsKey, enabled = true) {
  return useQuery({
    queryKey: planKeys.cards(k),
    queryFn: ({ signal }) =>
      planApi.cards(
        {
          platform: k.platform,
          board_id: k.board ?? undefined,
          from: k.from ?? undefined,
          to: k.to ?? undefined,
          archive: k.archive || undefined,
        },
        signal,
      ),
    enabled,
    // Смена вида или диапазона той же доски — прежние карточки, пока грузятся новые.
    placeholderData: (prev, prevQuery) => {
      const p = prevQuery?.queryKey[2] as CardsKey | undefined
      return p && p.platform === k.platform && p.board === k.board ? prev : undefined
    },
    ...FRESH,
  })
}

export function usePlanCard(id: number | null) {
  return useQuery({
    queryKey: planKeys.card(id ?? 0),
    queryFn: ({ signal }) => planApi.card(id ?? 0, signal),
    enabled: id !== null && id > 0,
    ...FRESH,
    staleTime: 0,
  })
}

/** В каких карточках пост: кнопка «В контент-план» в карточке поста. */
export function usePostPlan(postId: number) {
  return useQuery({
    queryKey: planKeys.postPlan(postId),
    queryFn: ({ signal }) => planApi.postPlan(postId, signal),
    enabled: Number.isFinite(postId) && postId > 0,
  })
}


const ERRORS: Record<string, string> = {
  owner_only: 'Это может только владелец',
  board_not_found: 'Доски нет или она в архиве',
  empty_title: 'Название не может быть пустым',
  title_taken: 'Доска с таким названием уже есть',
  source_not_found: 'Источник не найден',
  source_platform_mismatch: 'Источник другой площадки',
  source_taken: 'Этот источник уже свой у другой доски',
  last_board: 'Последнюю доску площадки убрать нельзя',
  bad_range: 'Неверный диапазон дат',
  not_found: 'Карточки нет: её удалили',
  bad_stage: 'Этап не той площадки',
  bad_url: 'Нужна ссылка, которая начинается с http:// или https://',
  platform_mismatch: 'Доска другой площадки',
  already_in_plan: 'Этот пост уже лежит на той доске',
  no_dates_for_stage: 'У этого этапа сроков не бывает',
  bad_dates: 'Конец раньше начала',
  unknown_member: 'Этого человека нет в команде',
  board_archived: 'Доска в архиве',
  post_not_found: 'Пост не найден',
  codex_disabled: 'Черновики Codex выключены: настройте вход на сервере',
  too_many_drafts: 'Слишком много черновиков, попробуйте через час',
  codex_failed: 'Codex не ответил, попробуйте ещё раз',
  too_many_attachments: 'У карточки уже 30 вложений',
  length_required: 'Файл не отправился, попробуйте ещё раз',
  video_use_link: 'Исходники роликов — ссылкой на диск',
  bad_type: 'Такой тип файла не принимаем',
  empty_file: 'Пустой файл',
  file_too_large: 'Файл больше 20 МБ',
  plan_budget_full: 'Место под файлы плана кончилось: уберите старые вложения',
  disk_full: 'На сервере мало места, попробуйте позже',
  incomplete: 'Загрузка оборвалась',
  bad_signature: 'Ссылка на файл устарела',
  network: 'Нет связи',
}

// Поля карточки и доски в 422 от Pydantic: что именно не прошло.
const TOO_LONG: Record<string, string> = {
  body: 'Текст слишком длинный',
  notes: 'Заметки слишком длинные',
  title: 'Заголовок слишком длинный',
  published_url: 'Ссылка на публикацию длиннее 500 знаков',
  url: 'Ссылка длиннее 1000 знаков',
  voice: '«Голос и тема» длиннее 2000 знаков',
}

export function planError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.detail && ERRORS[err.detail]) return ERRORS[err.detail] ?? ''
    if (err.status === 422) {
      const long = err.fields.find((f) => f.type === 'string_too_long' && TOO_LONG[f.name])
      if (long) return TOO_LONG[long.name] ?? ''
      return 'Проверьте поля'
    }
    if (err.status === 0) return ERRORS.network ?? ''
  }
  return 'Не получилось'
}


/** «В контент-план» из карточки поста. */
export function useAddFromPost() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: planApi.fromPost,
    onSuccess: ({ card }) => qc.setQueryData(planKeys.card(card.id), card),
    onSettled: (_d, _e, { post_id }) =>
      Promise.all([
        qc.invalidateQueries({ queryKey: planKeys.postPlan(post_id) }),
        qc.invalidateQueries({ queryKey: planKeys.cardsAll }),
      ]),
  })
}

